/**
 * Location Formatter Utility
 * Cleans and formats location strings to be readable by Google Maps
 */

// US State abbreviations map
const STATE_ABBREVIATIONS: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC',
};

// Reverse map for expanding abbreviations if needed
const STATE_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([name, abbr]) => [abbr, name])
);

// Valid US state abbreviations set
const VALID_STATE_ABBRS = new Set(Object.values(STATE_ABBREVIATIONS));

/**
 * Format a location string for Google Maps readability
 * @param location Raw location string
 * @returns Formatted location string
 */
export function formatLocationForMaps(location: string | undefined | null): string {
  if (!location || typeof location !== 'string') {
    return '';
  }

  let formatted = location.trim();
  
  // If empty after trim, return empty
  if (!formatted) {
    return '';
  }

  // Step 1: Normalize whitespace (replace multiple spaces/tabs/newlines with single space)
  formatted = formatted.replace(/\s+/g, ' ');

  // Step 2: Remove common unwanted characters but keep essential punctuation
  formatted = formatted.replace(/[^\w\s,.\-#'\/]/g, '');

  // Step 3: Fix common formatting issues
  // Add comma after city if followed by state abbreviation without comma
  // Pattern: "City ST" or "City State" -> "City, ST"
  formatted = formatted.replace(/(\b[A-Za-z]+)\s+([A-Z]{2})\b(?!\s*,)/g, '$1, $2');

  // Step 4: Ensure proper spacing around commas
  formatted = formatted.replace(/\s*,\s*/g, ', ');

  // Step 5: Handle ZIP codes - ensure space before 5-digit ZIP
  formatted = formatted.replace(/,?\s*(\d{5}(-\d{4})?)\s*$/g, ' $1');

  // Step 6: Convert full state names to abbreviations for consistency
  for (const [stateName, abbr] of Object.entries(STATE_ABBREVIATIONS)) {
    // Match full state name (case insensitive) followed by comma, space, or end
    const regex = new RegExp(`\\b${stateName}\\b`, 'gi');
    formatted = formatted.replace(regex, abbr);
  }

  // Step 7: Capitalize state abbreviations
  formatted = formatted.replace(/\b([a-z]{2})\b(?=\s*\d{5}|\s*$|,)/gi, (match) => {
    const upper = match.toUpperCase();
    if (VALID_STATE_ABBRS.has(upper)) {
      return upper;
    }
    return match;
  });

  // Step 8: Title case city names (but preserve state abbreviations)
  const parts = formatted.split(', ');
  const formattedParts = parts.map((part, index) => {
    // Don't title case if it's likely a state abbreviation or ZIP
    if (part.length === 2 && VALID_STATE_ABBRS.has(part.toUpperCase())) {
      return part.toUpperCase();
    }
    if (/^\d{5}(-\d{4})?$/.test(part)) {
      return part; // ZIP code
    }
    // Title case
    return part.replace(/\b\w+/g, (word) => {
      // Keep common words lowercase (except at start)
      const lowerWords = ['of', 'the', 'and', 'in', 'at', 'on'];
      if (index > 0 && lowerWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  });

  formatted = formattedParts.join(', ');

  // Step 9: Clean up any double commas or trailing commas
  formatted = formatted.replace(/,\s*,/g, ',');
  formatted = formatted.replace(/,\s*$/, '');
  formatted = formatted.replace(/^\s*,/, '');

  // Step 10: Final trim
  formatted = formatted.trim();

  return formatted;
}

/**
 * Parse a location string into structured address components
 * @param location Raw location string
 * @returns Parsed address object
 */
export function parseLocationToAddress(location: string): {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
} {
  if (!location) return {};

  const formatted = formatLocationForMaps(location);
  const parts = formatted.split(', ').map(p => p.trim()).filter(p => p);
  
  const result: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  } = {};

  // Try to identify ZIP code (5 digits or 5+4 format)
  const zipIndex = parts.findIndex(p => /^\d{5}(-\d{4})?$/.test(p));
  if (zipIndex !== -1) {
    result.zipCode = parts[zipIndex];
    parts.splice(zipIndex, 1);
  }

  // Try to identify state (2 letter abbreviation)
  const stateIndex = parts.findIndex(p => p.length === 2 && VALID_STATE_ABBRS.has(p.toUpperCase()));
  if (stateIndex !== -1) {
    result.state = parts[stateIndex].toUpperCase();
    parts.splice(stateIndex, 1);
  }

  // Check for country (common countries)
  const countryIndex = parts.findIndex(p => 
    /^(usa?|united states|canada|uk|united kingdom)$/i.test(p)
  );
  if (countryIndex !== -1) {
    result.country = parts[countryIndex];
    parts.splice(countryIndex, 1);
  }

  // Remaining parts: first is likely street, last is likely city
  if (parts.length >= 2) {
    result.street = parts.slice(0, -1).join(', ');
    result.city = parts[parts.length - 1];
  } else if (parts.length === 1) {
    // Could be just a city
    result.city = parts[0];
  }

  return result;
}

/**
 * Validate if a location string is likely valid for geocoding
 * @param location Location string to validate
 * @returns boolean indicating if location seems valid
 */
export function isValidLocation(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') {
    return false;
  }

  const trimmed = location.trim();
  
  // Too short to be useful
  if (trimmed.length < 3) {
    return false;
  }

  // Contains only numbers (not a valid location)
  if (/^\d+$/.test(trimmed)) {
    return false;
  }

  // Has at least some letters
  if (!/[a-zA-Z]/.test(trimmed)) {
    return false;
  }

  return true;
}

/**
 * Build a full address string from components
 * @param address Address components
 * @returns Formatted address string
 */
export function buildAddressString(address: {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}): string {
  const parts: string[] = [];
  
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(address.zipCode);
  if (address.country) parts.push(address.country);
  
  return parts.join(', ');
}

export default formatLocationForMaps;
